/**
 * recapiti.js — scrivere o telefonare a un paziente dalla sua scheda.
 *
 * IL RISCHIO QUI NON E' CHE NON FUNZIONI, E' CHE FUNZIONI VERSO QUALCUN
 * ALTRO. Un numero mal ricostruito non da' errore: apre una chat con una
 * persona che esiste e non c'entra niente, e quella persona riceve un
 * messaggio di un poliambulatorio che nomina un paziente. Per questo qui
 * dentro, quando il numero non e' riconoscibile con certezza, NON si
 * indovina: non si apre niente e si dice perche'.
 *
 * COM'E' SCRITTA LA RUBRICA, DAVVERO
 * Controllato sui 2.591 pazienti che hanno un numero (16 settembre 2026):
 *   2.546  cellulari italiani, cifre attaccate, senza prefisso  -> 3xxxxxxxxx
 *      19  fissi, che cominciano per 0                          -> 0541...
 *       1  con il prefisso internazionale scritto               -> +39333...
 *       3  cellulari italiani col 39 davanti ma senza il piu'
 *      41  fissi, numeri troppo corti, o sbagliati -> rifiutati
 *
 * Fra questi ultimi ce n'e' uno che spiega la regola meglio di qualsiasi
 * discorso: `33119330155`. Undici cifre che cominciano per 33. Puo' essere
 * un cellulare italiano battuto male, oppure un numero francese (+33). Non
 * c'e' modo di saperlo, e le due strade portano a due persone diverse:
 * quindi si rifiuta.
 */

/* Lunghezza minima perche' un numero internazionale sia plausibile: sotto
   questa soglia e' un errore di battitura, non un recapito. */
const MINIMO_INTERNAZIONALE = 8;

/**
 * Porta un numero di rubrica nella forma che vuole WhatsApp: solo cifre,
 * prefisso internazionale compreso, senza il `+`.
 *
 * @param {string} grezzo  il numero come sta in anagrafica
 * @returns {{numero: string|null, motivo: string|null}}
 */
export function perWhatsApp(grezzo) {
    const s = String(grezzo || '').trim();
    if (!s) return { numero: null, motivo: 'vuoto' };

    const internazionale = s.startsWith('+') || s.startsWith('00');
    const cifre = s.replace(/\D/g, '');

    /* Chi ha scritto il prefisso internazionale ha gia' detto di che paese
       e': non c'e' niente da indovinare. Si toglie solo lo zero-zero, che
       e' il modo europeo di scrivere il `+`. */
    if (internazionale) {
        const n = cifre.replace(/^00/, '');
        return n.length >= MINIMO_INTERNAZIONALE
            ? { numero: n, motivo: null }
            : { numero: null, motivo: 'corto' };
    }

    /* Cellulare italiano: comincia per 3 ed e' lungo nove o dieci cifre. */
    if (/^3\d{8,9}$/.test(cifre)) return { numero: `39${cifre}`, motivo: null };

    /* Lo stesso numero col 39 davanti ma senza il piu'. Undici o dodici
       cifre: `393406765428`. Non e' ambiguo come gli altri undici-cifre,
       perche' dopo il 39 resta esattamente un cellulare italiano valido.
       Questa riga esiste perche' la regola qui sopra e' stata provata sui
       2.591 numeri veri della rubrica e ne rifiutava tre di buoni: a
       ragionarci sopra non sarebbe venuto in mente. */
    if (/^393\d{8,9}$/.test(cifre)) return { numero: cifre, motivo: null };

    return { numero: null, motivo: 'non e\' un cellulare' };
}

/**
 * Apre la chat WhatsApp col paziente. Va chiamata DENTRO il clic: una
 * finestra aperta dopo un'attesa viene bloccata come se fosse pubblicita'.
 *
 * @returns {string|null} null se e' andata, altrimenti il motivo da dire
 */
export function apriWhatsApp(telefono) {
    const { numero, motivo } = perWhatsApp(telefono);

    if (motivo === 'vuoto') {
        return 'Nessun numero di telefono registrato per questo paziente.';
    }
    if (!numero) {
        return `Il numero registrato (${String(telefono).trim()}) non e' un cellulare `
             + 'riconoscibile, e WhatsApp scrive solo ai telefonini. '
             + 'Va corretto in anagrafica.';
    }

    /* `noopener` non e' formalita': senza, la pagina che si apre puo'
       toccare quella da cui e' partita, che qui e' una cartella clinica. */
    window.open(`https://wa.me/${numero}`, '_blank', 'noopener,noreferrer');
    return null;
}

export default apriWhatsApp;
